import PropTypes from 'prop-types';

/**
 * BloomCalendar - Jahresübersicht des Blühbilds (#270 / #609, Layout #633)
 *
 * Raster 6 Spalten × 2 Zeilen (Jan–Jun / Jul–Dez) statt 12 schmaler Spalten —
 * bei 300 px Seitenleiste bzw. 288 px Floating-Panel sind die Zellen damit
 * ≥ 44 px breit (Touch-Target, Design-Review-Befund). Je Zelle Farbtupfer der
 * dann blühenden Arten (max. MAX_DOTS, darüber „+n"). Der aktuell gewählte
 * Monat ist markiert. „Ruhige Monate" (Blühlücken) werden dezent gezeigt —
 * ohne Mängel-Rhetorik (Markenton). Klick springt in die Monatsansicht.
 */

const MONTH_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const MONTH_NAMES = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const MAX_DOTS = 4;

const BloomCalendar = ({ calendar, currentMonth, onSelectMonth }) => (
  <div className="grid grid-cols-6 gap-0.5" role="group" aria-label="Blühkalender">
    {calendar.map(({ month, species, count }) => {
      const isCurrent = month === currentMonth;
      const dots = species.slice(0, MAX_DOTS);
      const overflow = count - dots.length;
      const label =
        count === 0
          ? `${MONTH_NAMES[month]} — ruhiger Monat`
          : `${MONTH_NAMES[month]} — ${count} blühende ${count === 1 ? 'Art' : 'Arten'}`;

      return (
        <button
          key={month}
          type="button"
          aria-label={label}
          aria-current={isCurrent ? 'true' : undefined}
          onClick={() => onSelectMonth(month)}
          className={`flex min-h-[44px] flex-col items-center justify-start gap-1 rounded-md px-0.5 py-1 transition-colors ${
            isCurrent ? 'bg-pink-50 ring-1 ring-pink-300' : 'hover:bg-gray-50'
          }`}
        >
          <span
            className={`text-[10px] font-medium ${isCurrent ? 'text-pink-700' : 'text-gray-400'}`}
          >
            {MONTH_SHORT[month]}
          </span>
          <span className="flex flex-wrap items-center justify-center gap-0.5">
            {count === 0 ? (
              // Ruhiger Monat: leiser Punkt, kein Mangel
              <span
                className="mt-0.5 inline-block h-1 w-1 rounded-full bg-gray-200"
                aria-hidden="true"
              />
            ) : (
              <>
                {dots.map((s) => (
                  <span
                    key={s.key}
                    className="inline-block h-1.5 w-1.5 rounded-full ring-1 ring-black/10"
                    style={{ backgroundColor: s.color || '#D1D5DB' }}
                    aria-hidden="true"
                  />
                ))}
                {overflow > 0 && (
                  <span className="text-[9px] leading-none text-gray-400" aria-hidden="true">
                    +{overflow}
                  </span>
                )}
              </>
            )}
          </span>
        </button>
      );
    })}
  </div>
);

BloomCalendar.propTypes = {
  calendar: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.number.isRequired,
      count: PropTypes.number.isRequired,
      species: PropTypes.arrayOf(
        PropTypes.shape({
          key: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          color: PropTypes.string,
        })
      ).isRequired,
    })
  ).isRequired,
  currentMonth: PropTypes.number.isRequired,
  onSelectMonth: PropTypes.func.isRequired,
};

export default BloomCalendar;
